import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { format, isValid } from 'date-fns';

const { FiX, FiUser, FiPhone, FiMapPin, FiAward, FiBriefcase, FiSmartphone, FiShield, FiFlag, FiCreditCard } = FiIcons;

const DetailItem = ({ icon, label, value, subValue, highlight = false, statusVariant = false, statusType = 'default' }) => {
  const getStatusColor = (val) => {
    // Owner Status Logic
    if (val === 'Confirmed') return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (val === 'Pending') return 'text-amber-600 bg-amber-50 border-amber-100';
    if (val === 'No') return 'text-red-600 bg-red-50 border-red-100';
    
    // PAN Status Logic
    if (val === 'Not Required') return 'text-surface-500 bg-surface-50 border-surface-200';
    
    return 'text-surface-900';
  };

  return (
    <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-surface-50 transition-colors border border-transparent hover:border-surface-100">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${highlight ? 'bg-primary-100 text-primary-600' : 'bg-surface-100 text-surface-400'}`}>
        <SafeIcon icon={icon} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black text-surface-400 uppercase tracking-widest mb-0.5">{label}</p>
        <div className="flex items-center gap-2">
          <p className={`text-sm font-bold truncate ${highlight ? 'text-primary-700' : 'text-surface-900'} ${statusVariant ? 'px-2 py-0.5 rounded-lg border text-[11px] uppercase tracking-tighter ' + getStatusColor(value) : ''}`}>
            {value || '—'}
          </p>
        </div>
        {subValue && <p className="text-[10px] font-bold text-surface-400 mt-0.5 uppercase tracking-tighter leading-relaxed">{subValue}</p>}
      </div>
    </div>
  );
};

const UserDetailsModal = ({ mobileNo, onClose }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['user_details', mobileNo],
    queryFn: async () => {
      const response = await api.get('user_details.php', { params: { mobile_no: mobileNo } });
      return response.data?.data;
    },
    enabled: !!mobileNo,
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return isValid(date) ? format(date, 'PPP p') : dateStr;
  };

  const getShopOwnerStatus = (status) => {
    if (status === null || status === undefined || status === '') return "Pending";
    if (status === '1' || status === 'Yes') return "Confirmed";
    if (status === '0' || status === 'No') return "No";
    return status;
  };

  const getPanStatus = (pan) => {
    if (!pan || pan.trim() === '' || pan.toLowerCase() === 'null') return "Pending";
    if (pan === 'Not Required') return "Not Required";
    return pan; // Return the PAN Number
  };

  const modalContent = (
    <AnimatePresence>
      {mobileNo && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[999] bg-surface-950/40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 right-0 z-[1000] w-full max-w-lg bg-white shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-surface-100 flex items-center justify-between bg-white sticky top-0 z-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary-600 flex items-center justify-center text-white shadow-lg shadow-primary-200">
                  <SafeIcon icon={FiUser} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-surface-900 leading-none">User Profile</h3>
                  <p className="text-xs text-surface-400 font-bold mt-1 uppercase tracking-wider">System ID: {mobileNo}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2.5 hover:bg-surface-100 rounded-xl transition-colors">
                <SafeIcon icon={FiX} className="w-5 h-5 text-surface-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              {isLoading ? (
                <div className="space-y-6">
                  <Skeleton className="h-40 w-full rounded-3xl" />
                  <Skeleton className="h-40 w-full rounded-3xl" />
                </div>
              ) : isError ? (
                <div className="py-20 text-center">
                  <SafeIcon icon={FiFlag} className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-surface-900 font-black uppercase text-sm">Profile Load Failed</p>
                  <Button variant="outline" className="mt-4 rounded-xl" onClick={onClose}>Close Panel</Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-primary-600 uppercase tracking-[0.2em] ml-1">Business Verification</h4>
                    <div className="grid grid-cols-1 gap-1">
                      <DetailItem icon={FiBriefcase} label="Outlet Name" value={data.outlet_name} subValue={data.updated_outlet_name ? `Verified as: ${data.updated_outlet_name}` : 'Original Identity'} highlight={!!data.updated_outlet_name} />
                      <DetailItem icon={FiShield} label="Owner Status" value={getShopOwnerStatus(data.shop_owner)} statusVariant={true} />
                      <DetailItem icon={FiCreditCard} label="PAN Card" value={getPanStatus(data.pan_card)} statusVariant={getPanStatus(data.pan_card) === 'Pending' || getPanStatus(data.pan_card) === 'Not Required'} />
                      <DetailItem icon={FiAward} label="Achieved Slab" value={data.achieved_slab} />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-primary-600 uppercase tracking-[0.2em] ml-1">Contact Intelligence</h4>
                    <div className="grid grid-cols-1 gap-1">
                      <DetailItem icon={FiPhone} label="Primary Number" value={data.mobile_no} />
                      <DetailItem icon={FiSmartphone} label="Alternate Number" value={data.alternate_mobile_no} />
                      <DetailItem icon={FiFlag} label="Language / Preferred" value={`${data.language} / ${data.preferred_language}`} />
                    </div>
                  </div>

                  <div className="bg-primary-50/50 rounded-[2rem] p-6 border border-primary-100 shadow-sm shadow-primary-100/50">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center text-white">
                        <SafeIcon icon={FiAward} className="w-5 h-5" />
                      </div>
                      <h4 className="text-sm font-black text-primary-900 uppercase">Rewards Status</h4>
                    </div>
                    <div className="space-y-4">
                      {data.selected_gift ? (
                        <div className="bg-white p-4 rounded-2xl border border-primary-200 shadow-sm">
                          <p className="text-[10px] font-black text-primary-500 uppercase tracking-widest mb-1">Final Selection</p>
                          <p className="text-lg font-black text-surface-900">{data.selected_gift}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-2">
                          <p className="text-[10px] font-black text-surface-400 uppercase tracking-widest pl-1 mb-1">Available Options</p>
                          {[data.gift_option_1, data.gift_option_2, data.gift_option_3].map((g, i) => g && (
                            <div key={i} className="bg-white/60 p-3 rounded-xl border border-primary-100 text-sm font-bold text-surface-600">{g}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-primary-600 uppercase tracking-[0.2em] ml-1">Physical Address</h4>
                    <div className="grid grid-cols-1 gap-1">
                      <DetailItem 
                        icon={FiMapPin} 
                        label="Full Address" 
                        value={`${data.shop_no || ''}, ${data.area_name || ''}`} 
                        subValue={data.selected_landmark ? `Landmark: ${data.selected_landmark}` : ''} 
                      />
                      <DetailItem 
                        icon={FiMapPin} 
                        label="Region" 
                        value={`${data.city || ''}, ${data.state || ''}`} 
                        subValue={`Pincode: ${data.pincode || '—'}`} 
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-surface-100 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[9px] font-black text-surface-400 uppercase tracking-widest mb-1">Registered On</p>
                      <p className="text-[11px] font-bold text-surface-700">{formatDate(data.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-surface-400 uppercase tracking-widest mb-1">Last Update</p>
                      <p className="text-[11px] font-bold text-surface-700">{formatDate(data.updated_at)}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="p-6 bg-surface-50 border-t border-surface-100">
               <Button onClick={onClose} className="w-full h-12 rounded-2xl bg-surface-900 hover:bg-black font-black uppercase text-xs tracking-widest">Close Profile</Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default UserDetailsModal;